import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';
import { Helmet } from 'react-helmet';

import { splitStringArray } from '~/utils/splitStringArray';

import { SeoProps, SeoQuery } from './models';

const Seo: FC<SeoProps> = ({
  locale,
  urls,
  seo: {
    title,
    description,
    keywords,
    externalHreflangs,
    additionalMeta,
    canonicalUrl,
    pageType = 'website',
  },
  pageUid,
  fullPath,
}) => {
  const {
    allContentstackSettings: { nodes },
    site: {
      siteMetadata: { siteUrl },
    },
  } = useStaticQuery<SeoQuery>(graphql`
    query Seo {
      allContentstackSettings {
        nodes {
          siteName: site_name
          defaultLocale: default_locale
          tags
          publishDetails: publish_details {
            locale
          }
        }
      }
      site {
        siteMetadata {
          siteUrl
        }
      }
    }
  `);

  const { siteName, defaultLocale } =
    nodes.find(
      ({ tags, publishDetails: { locale: publishLocale } }) =>
        tags.includes(process.env.GATSBY_CONTENTSTACK_BRAND_TAG as string) &&
        publishLocale === locale
    ) || {};

  const titleTemplate = title === siteName ? '%s' : `%s | ${siteName}`;
  const siteLang = locale || defaultLocale;

  const meta = [
    {
      name: 'description',
      content: description,
    },
    {
      property: 'og:title',
      content: title,
    },
    {
      property: 'og:description',
      content: description,
    },
    {
      property: 'og:type',
      content: pageType,
    },
    {
      name: 'twitter:card',
      content: 'summary',
    },
    {
      name: 'twitter:title',
      content: title,
    },
    {
      name: 'twitter:description',
      content: description,
    },
    ...(keywords?.length
      ? [
          {
            name: 'keywords',
            content: splitStringArray(keywords).join(', '),
          },
        ]
      : []),
    ...additionalMeta.map(({ content, name, property }) => ({
      ...(name && { name }),
      ...(property && { property }),
      content,
    })),
  ];

  const link = fullPath
    ? [
        {
          rel: 'canonical',
          href: canonicalUrl || `${siteUrl}${fullPath}`,
        },
        ...urls.map(({ locale: hreflang, url }) => ({
          rel: 'alternate',
          href: `${siteUrl}${url}`,
          hreflang,
        })),
        ...externalHreflangs.map(({ hreflang, url: { href } }) => ({
          rel: 'alternate',
          href,
          hreflang,
        })),
      ]
    : null;

  return (
    // TODO: replace helmet with buildin Head feature, to rethink - https://dev.azure.com/RB-Group/IT_and_D_Project/_workitems/edit/249204
    <Helmet
      htmlAttributes={{ lang: siteLang, 'data-page-uid': pageUid }}
      {...{ title, titleTemplate, meta, link }}
    />
  );
};

export default Seo;
